import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import { useClickOutsideComponent, useForwardedRef } from '@hcs/hooks';

import { SearchIcon } from '../../../../svgs/icons';
import { SearchInputStyle } from '../SearchInputStyle';

import { AutoCompleteOptionType } from './AutoComplete.types';

import styles from './AutoCompleteLite.module.css';

interface AutoCompleteTheme {
  Input?: string;
  InputContainer?: string;
  OptionsContainer?: string;
  Option?: {
    Option?: string;
    OptionResultSelected?: string;
  };
  SearchInputStyle?: string;
}

type AutoCompleteInputStyle = 'form' | 'transparent' | 'search';

/**
 * A lightweight version of the AutoComplete component that does not allow filtering of options from within and
 * doesn't support multi-select.
 *
 * Created out of necessity due to the complexity and poor design choices in AutoComplete, this component should
 * be used whenever possible. It contains keyboard navigation and accessibility fixes that make AutoComplete
 * incompatible with groups of options, such as in the PlacesSearch implementation.
 * TODO migrate all uses of AutoComplete to this component.
 */
export interface AutoCompleteLiteProps<OptionValueType> {
  dataHcName: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string | null;
  role?: string;
  name?: string;
  inputStyle?: AutoCompleteInputStyle;
  options: AutoCompleteOptionType<OptionValueType>[];
  onHideResults?: VoidFunction;
  onFocus?: VoidFunction;
  onInputChange?: (value: string) => void;
  onSelectResult: (
    v: OptionValueType | null,
    manualControls?: {
      setSearch: React.Dispatch<React.SetStateAction<string>>;
      setSelectedOption: React.Dispatch<
        React.SetStateAction<AutoCompleteOptionType<OptionValueType> | null>
      >;
      setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
    },
  ) => void;
  icon?: ReactNode;
  clearAfterSelect?: boolean;
  theme?: AutoCompleteTheme;
  resultsHelper?: ReactNode;
  noOptionsContent?: ReactNode;
  isLoading?: boolean;
  activeOnMount?: boolean;
  focusOnMount?: boolean;
  enterToSelect?: boolean;
  initialInputValue?: string;
}

const AutoCompleteInner = <OptionValueType,>(
  props: AutoCompleteLiteProps<OptionValueType>,
  inputRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const {
    dataHcName,
    className,
    disabled,
    placeholder,
    role,
    name,
    options,
    onHideResults,
    onFocus,
    onInputChange,
    onSelectResult,
    inputStyle = 'form',
    icon,
    clearAfterSelect,
    theme,
    resultsHelper,
    noOptionsContent,
    isLoading,
    activeOnMount,
    focusOnMount,
    enterToSelect = true,
    initialInputValue,
  } = props;

  const [isActive, setIsActive] = useState(activeOnMount || false);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<AutoCompleteOptionType<OptionValueType> | null>(null);
  const [highlightedOption, setHighlightedOption] =
    useState<AutoCompleteOptionType<OptionValueType> | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const forwardedInputRef = useForwardedRef<HTMLInputElement>(inputRef);
  const interactiveOptions = options.filter((opt) => !opt.nonInteractive);

  useEffect(() => {
    if (focusOnMount) {
      forwardedInputRef.current?.focus();
    }
  }, [focusOnMount, forwardedInputRef]);

  const showResults = () => {
    setIsActive(true);
    // Clear selected option to allow user to change input value and select a different option when clearAfterSelect=false
    setSelectedOption(null);
    setHighlightedOption(null);
  };

  const hideResults = useCallback(() => {
    setIsActive(false);
    setHighlightedOption(null);
    onHideResults?.();
  }, [onHideResults]);

  useClickOutsideComponent(containerRef, hideResults);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value || '';
    setSearch(newSearch);
    onInputChange?.(newSearch); // Calling this imperatively because calling in a useEffect can lead to infinite loop
    setHighlightedOption(null);
  };

  const handleSelect = (option: AutoCompleteOptionType<OptionValueType>) => {
    if (clearAfterSelect) {
      setSelectedOption(null);
      setSearch('');
      onInputChange?.(''); // Calling this imperatively because calling in a useEffect can lead to infinite loop
    } else {
      setSelectedOption(option);
    }
    setIsActive(false);
    setHighlightedOption(null);

    onSelectResult(option.value, { setSearch, setSelectedOption, setIsActive });
  };

  const handleArrowDown = () => {
    if (interactiveOptions.length === 0) return;

    const currentIdx = highlightedOption
      ? interactiveOptions.findIndex(
          (opt) => opt.value === highlightedOption.value,
        )
      : -1;

    // If on last item, wrap to input (clear highlighted option)
    if (currentIdx === interactiveOptions.length - 1) {
      setHighlightedOption(null);
      return;
    }

    const nextOption = interactiveOptions[currentIdx + 1];
    if (nextOption) {
      setHighlightedOption(nextOption);
    }
  };

  const handleArrowUp = () => {
    if (interactiveOptions.length === 0) return;

    const currentIdx = highlightedOption
      ? interactiveOptions.findIndex(
          (opt) => opt.value === highlightedOption.value,
        )
      : -1;

    // If no option highlighted, go to last option
    if (currentIdx === -1) {
      const lastOption = interactiveOptions[interactiveOptions.length - 1];
      if (lastOption) {
        setHighlightedOption(lastOption);
      }
      return;
    }

    // If on first item, go to input (clear highlighted option)
    if (currentIdx === 0) {
      setHighlightedOption(null);
      return;
    }

    const prevOption = interactiveOptions[currentIdx - 1];
    if (prevOption) {
      setHighlightedOption(prevOption);
    }
  };

  const inputValue =
    initialInputValue ??
    (highlightedOption?.searchString || selectedOption?.searchString || search);
  const NoOptionsContent =
    typeof noOptionsContent !== 'undefined' ? noOptionsContent : 'No Options';

  return (
    <div
      data-hc-name={dataHcName}
      className={classNames(styles.AutoCompleteContainer, className, {
        [styles.transparentInput]:
          inputStyle === 'transparent' || inputStyle === 'search',
      })}
    >
      <SearchInputStyle
        className={classNames(styles.SearchInputStyle, theme?.SearchInputStyle)}
        unstyled={inputStyle !== 'search'}
        icon={
          icon ||
          (inputStyle === 'search' ? (
            <SearchIcon
              dataHcName={'search-icon'}
              size="sm"
              onClick={() => {
                forwardedInputRef.current?.focus();
              }}
            />
          ) : null)
        }
      >
        <div
          className={classNames(
            {
              [styles.Active]: isActive,
            },
            styles.AutoComplete,
          )}
          ref={containerRef}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              hideResults();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              handleArrowDown();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              handleArrowUp();
            } else if (e.key === 'Enter' && enterToSelect) {
              e.preventDefault();
              const firstInteractiveOption = interactiveOptions[0];
              if (highlightedOption) {
                handleSelect(highlightedOption);
              } else if (firstInteractiveOption) {
                handleSelect(firstInteractiveOption);
              }
            }
          }}
          data-hc-name={`${dataHcName}-key-trigger`}
        >
          <div
            className={classNames(styles.InputContainer, theme?.InputContainer)}
          >
            <input
              name={name}
              role={role || 'combobox'}
              aria-expanded={isActive}
              aria-haspopup="listbox"
              disabled={disabled}
              className={classNames(styles.InputElement, theme?.Input)}
              data-hc-name={`${dataHcName}-input`}
              placeholder={placeholder || undefined}
              value={inputValue}
              onChange={handleSearch}
              onFocus={(e) => {
                if (selectedOption) {
                  e.target.select();
                }
                showResults();
                onFocus?.();
              }}
              ref={forwardedInputRef}
              autoComplete="off"
            />
          </div>
          <div
            role="listbox"
            aria-label={'AutoComplete Search Results'}
            data-hc-name={`${dataHcName}-options`}
            className={classNames(
              styles.OptionsContainer,
              theme?.OptionsContainer,
            )}
          >
            {options.map((option, idx) => {
              const highlighted = option.value === highlightedOption?.value;
              return (
                <div
                  key={`${idx}-${
                    typeof option.value === 'string' ||
                    typeof option.value === 'number'
                      ? option.value
                      : ''
                  }`}
                  role="option"
                  aria-selected={highlighted}
                  tabIndex={0}
                  data-hc-name={`${dataHcName}-option`}
                  data-hc-event-name={option.dataHcEventName}
                  data-hc-event-type={option.dataHcEventType}
                  data-hc-event-section={option.dataHcEventSection}
                  onClick={() => {
                    if (!option.nonInteractive) {
                      handleSelect(option);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && handleSelect) {
                      handleSelect(option);
                    }
                  }}
                  className={classNames(
                    styles.Option,
                    theme?.Option?.Option,
                    option.className,
                    {
                      [styles.darkHighlight]: highlighted,
                      [styles.nonInteractive || '']: option.nonInteractive,
                    },
                  )}
                >
                  {option.label}
                </div>
              );
            })}
            {options.length === 0 &&
              !isLoading &&
              inputValue &&
              NoOptionsContent && (
                <div
                  data-hc-name={`${dataHcName}-no-options`}
                  className={classNames(
                    styles.NoOptions,
                    theme?.Option?.Option,
                  )}
                >
                  {NoOptionsContent}
                </div>
              )}
            {resultsHelper && inputValue && (
              <div
                data-hc-name={`${dataHcName}-results-helper`}
                className={styles.ResultsHelper}
              >
                {resultsHelper}
              </div>
            )}
          </div>
        </div>
      </SearchInputStyle>
    </div>
  );
};

export const AutoCompleteLite = React.forwardRef(AutoCompleteInner);
